// Issue #143 sub-step 6: SDK migration to Amplify v6 + react-router v6.
// v5 `useHistory().push(path)` -> v6 `useNavigate()(path)`.
// v5 `Auth.signOut({ global: true })` -> v6 `signOut({ global: true })`
// from `aws-amplify/auth`.
//
// Pre-MR-!42 review fix: the seven `isRecommender(user.username)` sites
// below previously called a util that returned `true` for any username
// other than the two legacy Gen 1 literals. After App.tsx flipped to
// group-based routing (operator Q6), every real Admin/Grader in the
// Gen 2 pool was misclassified as a recommender by this NavBar -- wrong
// title, hidden home/sync/search icons, "Reset password" menu item
// shown. Now reads `cognito:groups` via `useAuthGroups()` and derives
// `isRecommenderUser` once at the top of the component.
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { signOut as amplifySignOut } from 'aws-amplify/auth';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import SyncIcon from '@mui/icons-material/Sync';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchBox from './SearchBox';
import { DataContext, fetchApplicantsList } from '../DataContextProvider';
import { useLang } from '../LanguageContextProvider';
import { useAuthGroups, isRecommenderByGroups, isAdminByGroups } from '../auth-groups';

const SearchBoxWrapper = styled(Box)(({ theme: { spacing } }) => ({
    padding: spacing(0, 3)
}));

const NavBar = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = !!anchorEl;
    const { dispatch } = useContext(DataContext);
    const { lang, setLang } = useLang();
    // Single derivation point per render -- the seven gate sites below
    // all consume this. Reads the same `cognito:groups` claim App.tsx
    // already supplied via `AuthGroupsContext`.
    const groups = useAuthGroups();
    const isRecommenderUser = isRecommenderByGroups(groups);
    const isAdminUser = isAdminByGroups(groups);


    const signOut = async () => {
        setAnchorEl(null);
        try {
            await amplifySignOut({ global: true });
            window.location.reload();
        } catch (error) {
            console.log('error signing out: ', error);
        }
    };

    const handleReload = () => {
        if (isRecommenderUser) {
            // To Do
        } else {
            if (dispatch) fetchApplicantsList(dispatch);

        }
    };

    return (
        <AppBar position="static">
            <Toolbar>
                <ButtonBase disableRipple onClick={() => {
                    navigate('/');
                }}>
                    <Typography variant="h6" color="inherit">{isRecommenderUser ? (lang === 'en' ? 'School Report & Recommendation Letter Uploader' : '調査書・推薦状アップローダー') : 'GBFDX App'}</Typography>
                </ButtonBase>
                <Box flex={1} />
                {isRecommenderUser ? (
                    <Box
                        role="group"
                        aria-label="Language / 言語"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}
                    >
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setLang('en')}
                            aria-label="English"
                            aria-pressed={lang === 'en'}
                            sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: '1rem',
                                fontWeight: lang === 'en' ? 'bold' : 'normal',
                                color: lang === 'en' ? 'inherit' : 'rgba(255, 255, 255, 0.6)',
                            }}
                        >
                            EN
                        </Box>
                        <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            /
                        </Typography>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setLang('ja')}
                            aria-label="日本語"
                            aria-pressed={lang === 'ja'}
                            sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: '1rem',
                                fontWeight: lang === 'ja' ? 'bold' : 'normal',
                                color: lang === 'ja' ? 'inherit' : 'rgba(255, 255, 255, 0.6)',
                            }}
                        >
                            JP
                        </Box>
                    </Box>
                ) : null}
                {isRecommenderUser ? null : (
                    <SearchBoxWrapper>
                        <SearchBox />
                    </SearchBoxWrapper>
                )}
                <Box display="flex" justifyContent="space-around">
                    {isRecommenderUser ? null : (
                        <IconButton color="inherit" onClick={handleReload}>
                            <SyncIcon />
                        </IconButton>
                    )}
                    {isRecommenderUser ? null : (
                        <IconButton color="inherit" onClick={() => navigate('/')}>
                            <HomeIcon />
                        </IconButton>
                    )}
                    {isAdminUser ? (
                        <IconButton color="inherit" aria-label="Dashboard" onClick={() => navigate('/dashboard')}>
                            <DashboardIcon />
                        </IconButton>
                    ) : null}
                    <IconButton color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
                        <AccountCircleIcon />
                    </IconButton>
                    <Menu open={open} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
                        {isRecommenderUser ? (<MenuItem>{lang === 'en' ? 'Reset password' : 'パスワードの変更'}</MenuItem>) : null}
                        <MenuItem onClick={signOut}>{lang === 'en' ? 'Log out' : 'ログアウト'}</MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default NavBar;
